import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle, ArrowRight, Phone, BarChart3, Globe, Shield, Zap, DollarSign, Star, Users, Bot, Layers, Wallet, Clock } from 'lucide-react';
import SiteFooter from './SiteFooter';
import SEOHead from './SEOHead';

const FEATURES = [
  { name: 'Call Tracking (CallRail)', us: true, them: false, category: 'tracking' },
  { name: 'Dynamic Number Insertion (DPNI)', us: true, them: false, category: 'tracking' },
  { name: 'Call Recording & Attribution', us: true, them: false, category: 'tracking' },
  { name: 'Citation Builder / Tracker', us: true, them: true, category: 'citations' },
  { name: 'White-Label Client Dashboard', us: true, them: true, category: 'agency' },
  { name: 'White-Label Mobile App', us: false, them: true, category: 'agency' },
  { name: 'CRM & Pipeline Management', us: false, them: true, category: 'crm' },
  { name: 'Client Impersonation (Admin View)', us: true, them: false, category: 'agency' },
  { name: 'Subscription Dunning System', us: true, them: false, category: 'billing' },
  { name: 'Failed Payment Auto-Recovery', us: true, them: false, category: 'billing' },
  { name: '3,750+ Programmatic SEO Pages', us: true, them: false, category: 'seo' },
  { name: 'AI-Powered Blog (Daily Auto-Posts)', us: true, them: false, category: 'content' },
  { name: 'Schema Stacking (7 Types)', us: true, them: false, category: 'seo' },
  { name: 'IndexNow + Google Indexing API', us: true, them: false, category: 'seo' },
  { name: 'Local SEO & Listings', us: true, them: true, category: 'seo' },
  { name: 'Review Monitoring', us: true, them: true, category: 'reputation' },
  { name: 'Review Generation Campaigns', us: true, them: true, category: 'reputation' },
  { name: 'SMS Campaigns ($0.049/msg)', us: true, them: false, category: 'marketing' },
  { name: 'Email Marketing', us: true, them: true, category: 'marketing' },
  { name: 'Stripe/PayPal Billing', us: true, them: true, category: 'billing' },
  { name: '250+ Marketplace Products', us: false, them: true, category: 'agency' },
  { name: 'AI Snapshot Reports', us: false, them: true, category: 'reporting' },
  { name: 'Proposal Builder', us: false, them: true, category: 'agency' },
  { name: 'Real-Time Lead Attribution', us: true, them: false, category: 'tracking' },
];

const PRICING = [
  { label: 'Vendasta Starter', price: '$99/mo', sub: '+ $500 onboarding', features: '1 seat, 10 reports, basic CRM' },
  { label: 'Vendasta Pro', price: '$499/mo', sub: '+ $1,000 onboarding', features: '5 seats, 20 clients, white-label' },
  { label: 'Vendasta Premium', price: '$999/mo', sub: '+ $1,500 onboarding', features: '10 seats, 200 clients, automation' },
];

const COLORS = { primary: '#002FA7', dark: '#0a0f1e', accent: '#FFCC00' };

export default function ComparisonVendasta() {
  const { t } = useTranslation('compare');
  const usWins = FEATURES.filter(f => f.us && !f.them).length;
  const theyWin = FEATURES.filter(f => !f.us && f.them).length;
  const both = FEATURES.filter(f => f.us && f.them).length;

  return (
    <>
      <SEOHead
        title="LocalBranding Pro vs Vendasta — Which Platform Is Better for Agencies? 2026"
        description="Compare LocalBranding Pro and Vendasta side-by-side. See why agencies switch from Vendasta's $99-999/mo plans to a platform with call tracking, AI content, and zero onboarding fees."
        canonical="https://localbrandingpro.com/compare/vendasta"
        keywords="LocalBranding Pro vs Vendasta, Vendasta alternative, local marketing platform, white label marketing, agency platform comparison"
      />
      <div style={{ fontFamily: "'Outfit', 'IBM Plex Sans', sans-serif" }}>
        <section className="relative overflow-hidden" style={{ background: COLORS.dark }}>
          <div className="absolute inset-0 opacity-10" style={{ background: `radial-gradient(circle at 30% 60%, ${COLORS.primary}, transparent 60%)` }} />
          <div className="max-w-6xl mx-auto px-6 py-20 relative z-10 text-center">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] mb-4 px-4 py-1.5 rounded-full" style={{ background: `${COLORS.accent}20`, color: COLORS.accent }}>
              Agency Platform Showdown 2026
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="comparison-title">
              LocalBranding Pro
              <span className="block text-2xl sm:text-3xl font-normal text-slate-400 mt-2">vs Vendasta</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-8">
              Vendasta charges $99-999/mo plus up to $1,500 in onboarding fees. We give you call tracking, AI content, and dunning — tools Vendasta doesn't have — without the markup.
            </p>
            <div className="flex flex-wrap justify-center gap-6 mb-8">
              <div className="text-center px-6 py-3 rounded-xl bg-white/5 border border-white/10">
                <p className="text-3xl font-bold" style={{ color: COLORS.accent }}>{usWins}</p>
                <p className="text-xs text-slate-400 mt-1">Features Only We Have</p>
              </div>
              <div className="text-center px-6 py-3 rounded-xl bg-white/5 border border-white/10">
                <p className="text-3xl font-bold text-white">{both}</p>
                <p className="text-xs text-slate-400 mt-1">Shared Features</p>
              </div>
              <div className="text-center px-6 py-3 rounded-xl bg-white/5 border border-white/10">
                <p className="text-3xl font-bold text-slate-500">{theyWin}</p>
                <p className="text-xs text-slate-400 mt-1">Vendasta Only</p>
              </div>
            </div>
            <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-lg text-white font-bold text-lg transition-all hover:scale-105" style={{ background: COLORS.primary }} data-testid="cta-hero">
              Try LocalBranding Pro Free <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </section>

        <section className="py-16 bg-white" data-testid="comparison-table-section">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-2" style={{ color: COLORS.dark }}>Side-by-side feature comparison</h2>
            <p className="text-center text-slate-500 text-sm mb-10">Everything agencies need to know before choosing a platform</p>
            <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <table className="w-full text-sm" data-testid="comparison-table">
                <thead><tr className="bg-slate-50">
                  <th className="text-left px-6 py-4 font-semibold text-slate-700">Feature</th>
                  <th className="text-center px-6 py-4 font-bold" style={{ color: COLORS.primary }}>LocalBranding Pro</th>
                  <th className="text-center px-6 py-4 font-semibold text-slate-500">Vendasta</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {FEATURES.map((f, i) => (
                    <tr key={i} className={`${f.us && !f.them ? 'bg-blue-50/30' : ''} hover:bg-slate-50`}>
                      <td className="px-6 py-3.5 text-slate-700">{f.name}</td>
                      <td className="px-6 py-3.5 text-center">{f.us ? <CheckCircle2 className="h-5 w-5 inline text-emerald-500" /> : <XCircle className="h-5 w-5 inline text-slate-300" />}</td>
                      <td className="px-6 py-3.5 text-center">{f.them ? <CheckCircle2 className="h-5 w-5 inline text-emerald-500" /> : <XCircle className="h-5 w-5 inline text-slate-300" />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="py-16" style={{ background: '#f8fafc' }}>
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10" style={{ color: COLORS.dark }}>Why agencies switch from Vendasta</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Wallet, title: 'No Onboarding Fees', desc: 'Vendasta charges $500-$1,500 just to get started. We charge zero. Start selling to clients on day one.' },
                { icon: Phone, title: 'Built-In Call Tracking', desc: 'CallRail integration shows which marketing channels drive phone calls. Vendasta can\'t track calls natively.' },
                { icon: Bot, title: 'AI Visibility Scanning', desc: 'See how your business appears in AI search results. Vendasta has AI reports but no AI discoverability tracking.' },
                { icon: Shield, title: 'Revenue Protection', desc: '4-stage dunning system protects your recurring revenue. Vendasta doesn\'t handle failed payments for you.' },
                { icon: Globe, title: '3,750+ SEO Pages', desc: 'Programmatic pages for every industry and city. Instant organic traffic that Vendasta\'s platform doesn\'t generate.' },
                { icon: Clock, title: 'Simple Pricing', desc: 'One plan, everything included. No per-seat charges, no marketplace markups, no surprise add-on fees.' },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-md transition-shadow">
                  <item.icon className="h-8 w-8 mb-4" style={{ color: COLORS.primary }} />
                  <h3 className="font-bold text-lg mb-2" style={{ color: COLORS.dark }}>{item.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10" style={{ color: COLORS.dark }}>{t('vendasta.pricingTitle')}</h2>
            <div className="grid md:grid-cols-3 gap-6 mb-10">
              {PRICING.map((p, i) => (
                <div key={i} className="rounded-xl border border-slate-200 p-6 text-center">
                  <p className="text-sm font-semibold text-slate-500 mb-2">{p.label}</p>
                  <p className="text-3xl font-bold mb-1" style={{ color: COLORS.dark }}>{p.price}</p>
                  <p className="text-xs text-red-500 font-medium mb-2">{p.sub}</p>
                  <p className="text-xs text-slate-400">{p.features}</p>
                </div>
              ))}
            </div>
            <div className="rounded-xl p-8 text-center" style={{ background: COLORS.primary }}>
              <p className="text-white/80 text-sm mb-2">LocalBranding Pro</p>
              <p className="text-4xl font-bold text-white mb-2">Zero onboarding. Everything included.</p>
              <p className="text-white/60 text-sm mb-6">Call tracking + Citations + AI Blog + Dunning + SMS + Dashboard — no per-seat fees</p>
              <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-3 rounded-lg font-bold transition-all hover:scale-105" style={{ background: COLORS.accent, color: COLORS.dark }} data-testid="cta-pricing">
                Get Started Free <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16" style={{ background: COLORS.dark }}>
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">{t('vendasta.readySwitch')}</h2>
            <p className="text-slate-400 mb-8">{t('vendasta.readySwitchSub')}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="px-8 py-4 rounded-lg font-bold text-lg transition-all hover:scale-105" style={{ background: COLORS.accent, color: COLORS.dark }}>Start Free Trial</Link>
              <Link to="/compare/brightlocal" className="px-8 py-4 rounded-lg font-bold text-lg border border-white/20 text-white hover:bg-white/10">vs BrightLocal</Link>
            </div>
          </div>
        </section>
        <SiteFooter />
      </div>
    </>
  );
}
